import Phaser from 'phaser';
import { BaseScene } from './BaseScene';
import { SceneKey } from './sceneKeys';
import { GAME_WIDTH, GAME_HEIGHT } from './gameConfig';
import { AIConfig, AILevel } from '../ai/types';
import { CustomAICharacter } from '../types/CustomAICharacter';
import {
  loadCustomAICharacters,
  saveCustomAICharacter,
  deleteCustomAICharacter,
  MAX_CUSTOM_AI_CHARACTERS,
} from '../utils/customAIManager';
import {
  DELETE_CONFIRM_MESSAGES,
  createDeleteConfirmState,
  handleConfirm,
  handleCancel,
  DeleteConfirmState,
} from '../utils/deleteConfirmDialog';
import { getSpeciesAIDefault } from '../constants/speciesAIDefaults';

// ─────────────────────────────────────
// 定数
// ─────────────────────────────────────

const MONSTER_IDS = ['zaag', 'gardan', 'roona', 'zephyr', 'balga', 'morsu', 'graon', 'igna'];

const MONSTER_NAMES: Record<string, string> = {
  zaag: 'レイン', gardan: 'ドルグ', roona: 'シエル', zephyr: 'カイ',
  balga: 'ゲンブ',  morsu: 'ユグド',  graon: 'ボルグ', igna: 'アーシュ',
};

const AI_LEVEL_VALUES: AILevel[] = [
  AILevel.LV1, AILevel.LV2, AILevel.LV3, AILevel.LV4, AILevel.LV5, AILevel.LV6,
];
const AI_LEVEL_LABELS: Record<AILevel, string> = {
  [AILevel.LV1]: 'Lv1', [AILevel.LV2]: 'Lv2', [AILevel.LV3]: 'Lv3',
  [AILevel.LV4]: 'Lv4', [AILevel.LV5]: 'Lv5', [AILevel.LV6]: 'Lv6（達人）',
};

type SliderKey = keyof Omit<AIConfig, 'thinkingLevel'>;

interface SliderDef {
  key: SliderKey;
  label: string;
  note?: string;
}

const LEFT_SLIDERS: SliderDef[] = [
  { key: 'aggression',         label: '攻撃性' },
  { key: 'specialPreference',  label: '特殊志向' },
  { key: 'distancePreference', label: '距離志向' },
  { key: 'stanceTendency',     label: 'スタンス' },
  { key: 'reflectorUsage',     label: 'リフレクター' },
  { key: 'riskTolerance',      label: 'リスク許容', note: 'Lv3+' },
];

const RIGHT_SLIDERS: SliderDef[] = [
  { key: 'patternReading',    label: 'パターン読み', note: 'Lv4+' },
  { key: 'counterTendency',   label: 'カウンター',   note: 'Lv4+' },
  { key: 'unpredictability',  label: '予測不能性',   note: 'Lv4+' },
  { key: 'adaptability',      label: '適応力',       note: 'Lv5' },
  { key: 'metaReading',       label: 'メタ読み',     note: 'Lv5' },
  { key: 'comboSynergy',      label: 'コンボ志向',   note: '未実装' },
];

enum AIEditState { LIST = 'LIST', CHAR_SELECT = 'CHAR_SELECT', PARAM_EDITOR = 'PARAM_EDITOR' }

// ─────────────────────────────────────
// シーン
// ─────────────────────────────────────

export class AICharacterEditScene extends BaseScene {
  private state: AIEditState = AIEditState.LIST;
  private uiElements: Phaser.GameObjects.GameObject[] = [];

  // 編集中データ
  private editingChar: CustomAICharacter | null = null;
  private editConfig!: AIConfig;
  private editName = '';
  private editMonsterId = '';

  // スライダー状態
  private draggingKey: SliderKey | null = null;
  private sliderTrackData = new Map<SliderKey, { x: number; width: number }>();
  private sliderFills = new Map<SliderKey, Phaser.GameObjects.Rectangle>();
  private sliderThumbs = new Map<SliderKey, Phaser.GameObjects.Arc>();
  private sliderValueTexts = new Map<SliderKey, Phaser.GameObjects.Text>();
  private levelButtonTexts: Phaser.GameObjects.Text[] = [];

  // DOM入力
  private nameDom: Phaser.GameObjects.DOMElement | null = null;

  // 削除確認ダイアログ
  private deleteConfirmState: DeleteConfirmState = createDeleteConfirmState();
  private dialogElements: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super(SceneKey.AI_CHARACTER_EDIT);
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#111122');
    this.setupDragListeners();
    this.showList();
  }

  // ─────────────────────────────────────
  // ドラッグリスナー
  // ─────────────────────────────────────

  private setupDragListeners(): void {
    this.input.on('pointermove', this.onGlobalPointerMove, this);
    this.input.on('pointerup', this.onGlobalPointerUp, this);
    this.events.on('shutdown', () => {
      this.input.off('pointermove', this.onGlobalPointerMove, this);
      this.input.off('pointerup', this.onGlobalPointerUp, this);
    });
  }

  private onGlobalPointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.draggingKey || !pointer.isDown) return;
    const track = this.sliderTrackData.get(this.draggingKey);
    if (!track) return;
    this.applySliderValue(this.draggingKey, pointer.x, track.x, track.width);
  }

  private onGlobalPointerUp(): void {
    this.draggingKey = null;
  }

  // ─────────────────────────────────────
  // UI クリア
  // ─────────────────────────────────────

  private clearUI(): void {
    this.uiElements.forEach((el) => el.destroy());
    this.uiElements = [];
    this.sliderTrackData.clear();
    this.sliderFills.clear();
    this.sliderThumbs.clear();
    this.sliderValueTexts.clear();
    this.levelButtonTexts = [];
    this.nameDom = null;
    this.draggingKey = null;
  }

  // ─────────────────────────────────────
  // LIST ビュー
  // ─────────────────────────────────────

  private showList(): void {
    this.clearUI();
    this.state = AIEditState.LIST;
    const chars = loadCustomAICharacters();
    const cx = GAME_WIDTH / 2;

    this.addText(cx, 28, 'AIキャラ一覧', { fontSize: '28px', color: '#ffd700', fontStyle: 'bold' }).setOrigin(0.5);

    if (chars.length === 0) {
      this.addText(cx, GAME_HEIGHT / 2 - 20, 'AIキャラが登録されていません', { fontSize: '18px', color: '#888888' }).setOrigin(0.5);
    }

    chars.forEach((char, i) => {
      this.createCharCard(char, 100 + i * 90);
    });

    if (chars.length < MAX_CUSTOM_AI_CHARACTERS) {
      this.addButton(cx, 465, '＋ 新規作成', () => this.showCharSelect()).setOrigin(0.5);
    } else {
      this.addText(cx, 465, `最大${MAX_CUSTOM_AI_CHARACTERS}体まで登録できます`, { fontSize: '15px', color: '#888888' }).setOrigin(0.5);
    }

    this.addButton(cx, 510, '← タイトルに戻る', () => this.transitionTo(SceneKey.TITLE)).setOrigin(0.5);
  }

  private createCharCard(char: CustomAICharacter, y: number): void {
    const cx = GAME_WIDTH / 2;
    const cardW = 780;
    const cardH = 75;

    const bg = this.add.rectangle(cx, y + cardH / 2, cardW, cardH, 0x1a1a3a).setStrokeStyle(1, 0x4444aa);
    this.uiElements.push(bg);

    const levelStr = char.aiConfig.thinkingLevel.replace('LV', 'Lv');
    this.addText(cx - 330, y + 14, char.name, { fontSize: '20px', color: '#ffffff' });
    this.addText(cx - 330, y + 40, `${MONSTER_NAMES[char.monsterId] ?? char.monsterId} ／ 思考${levelStr}`, { fontSize: '13px', color: '#8888cc' });

    const editBtn = this.addButton(cx + 240, y + cardH / 2, '編集', () => this.startEdit(char));
    editBtn.setOrigin(0.5);

    const delBtn = this.addButton(cx + 320, y + cardH / 2, '削除', () => {
      this.showDeleteConfirmDialog(char.id);
    });
    delBtn.setColor('#ff6666').setOrigin(0.5);
  }

  // ─────────────────────────────────────
  // 削除確認ダイアログ
  // ─────────────────────────────────────

  private showDeleteConfirmDialog(charId: string): void {
    if (this.deleteConfirmState.isOpen) return;

    this.deleteConfirmState.isOpen = true;
    this.deleteConfirmState.targetId = charId;
    this.deleteConfirmState.onConfirm = (id: string) => {
      deleteCustomAICharacter(id);
      this.showList();
    };
    this.deleteConfirmState.onClose = () => {
      this.closeDeleteConfirmDialog();
    };

    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;

    // オーバーレイ
    const overlay = this.add.rectangle(cx, cy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setDepth(100).setInteractive();
    this.dialogElements.push(overlay);

    // ダイアログ背景
    const dialogBg = this.add.rectangle(cx, cy, 360, 180, 0x1a1a3a)
      .setStrokeStyle(2, 0x6666cc).setDepth(101);
    this.dialogElements.push(dialogBg);

    // タイトル
    const title = this.add.text(cx, cy - 50, DELETE_CONFIRM_MESSAGES.title, {
      fontSize: '20px', color: '#ffd700', fontFamily: 'Arial', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(102);
    this.dialogElements.push(title);

    // 本文
    const body = this.add.text(cx, cy - 10, DELETE_CONFIRM_MESSAGES.body, {
      fontSize: '16px', color: '#ffffff', fontFamily: 'Arial',
    }).setOrigin(0.5).setDepth(102);
    this.dialogElements.push(body);

    // 「はい」ボタン
    const yesBtn = this.add.text(cx - 60, cy + 40, DELETE_CONFIRM_MESSAGES.yes, {
      fontSize: '18px', color: '#ff6666', fontFamily: 'Arial',
      stroke: '#000000', strokeThickness: 3,
      padding: { x: 12, y: 6 },
    }).setOrigin(0.5).setDepth(102).setInteractive({ useHandCursor: true });
    yesBtn.on('pointerover', () => yesBtn.setScale(1.08));
    yesBtn.on('pointerout', () => yesBtn.setScale(1.0));
    yesBtn.on('pointerdown', () => handleConfirm(this.deleteConfirmState));
    this.dialogElements.push(yesBtn);

    // 「いいえ」ボタン
    const noBtn = this.add.text(cx + 60, cy + 40, DELETE_CONFIRM_MESSAGES.no, {
      fontSize: '18px', color: '#88aaff', fontFamily: 'Arial',
      stroke: '#000000', strokeThickness: 3,
      padding: { x: 12, y: 6 },
    }).setOrigin(0.5).setDepth(102).setInteractive({ useHandCursor: true });
    noBtn.on('pointerover', () => noBtn.setScale(1.08));
    noBtn.on('pointerout', () => noBtn.setScale(1.0));
    noBtn.on('pointerdown', () => handleCancel(this.deleteConfirmState));
    this.dialogElements.push(noBtn);
  }

  private closeDeleteConfirmDialog(): void {
    this.dialogElements.forEach((el) => el.destroy());
    this.dialogElements = [];
  }

  // ─────────────────────────────────────
  // CHAR_SELECT ビュー（新規作成）
  // ─────────────────────────────────────

  private showCharSelect(): void {
    this.clearUI();
    this.state = AIEditState.CHAR_SELECT;
    const cx = GAME_WIDTH / 2;

    this.addText(cx, 28, 'ベースキャラを選択', { fontSize: '28px', color: '#ffd700', fontStyle: 'bold' }).setOrigin(0.5);
    this.addText(cx, 65, '選んだキャラの種族特性がデフォルト値に反映されます', { fontSize: '14px', color: '#aaaaaa' }).setOrigin(0.5);

    // 8キャラ: 2行 × 4列
    const cols = 4;
    const btnW = 210;
    const btnH = 120;
    const gapX = 15;
    const gapY = 20;
    const totalW = cols * btnW + (cols - 1) * gapX;
    const startX = (GAME_WIDTH - totalW) / 2 + btnW / 2;
    const startY = 160;

    MONSTER_IDS.forEach((id, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const bx = startX + col * (btnW + gapX);
      const by = startY + row * (btnH + gapY);

      const bg = this.add.rectangle(bx, by, btnW, btnH, 0x1a2a3a)
        .setStrokeStyle(2, 0x4466aa)
        .setInteractive({ useHandCursor: true });
      this.uiElements.push(bg);

      bg.on('pointerover', () => bg.setFillStyle(0x2a3a5a).setStrokeStyle(2, 0x88aaff));
      bg.on('pointerout', () => bg.setFillStyle(0x1a2a3a).setStrokeStyle(2, 0x4466aa));
      bg.on('pointerdown', () => this.startCreate(id));

      this.addText(bx, by - 20, MONSTER_NAMES[id], { fontSize: '20px', color: '#ffffff', fontStyle: 'bold' }).setOrigin(0.5);
      this.addText(bx, by + 12, `(${id})`, { fontSize: '12px', color: '#7788aa' }).setOrigin(0.5);
    });

    this.addButton(cx, 510, '← 戻る', () => this.showList()).setOrigin(0.5);
  }

  // ─────────────────────────────────────
  // PARAM_EDITOR ビュー
  // ─────────────────────────────────────

  private showParamEditor(): void {
    this.clearUI();
    this.state = AIEditState.PARAM_EDITOR;

    const title = this.editingChar === null ? '新規AIキャラ作成' : 'AIキャラ編集';
    this.addText(GAME_WIDTH / 2, 14, title, { fontSize: '22px', color: '#ffd700', fontStyle: 'bold' }).setOrigin(0.5);

    // 名前入力
    this.addText(25, 45, '名前:', { fontSize: '14px', color: '#cccccc' });
    this.nameDom = this.add.dom(190, 48, 'input', `
      width:200px; height:26px; font-size:14px; padding:2px 6px;
      background:#1a1a3a; color:#ffffff; border:1px solid #5555aa;
      border-radius:4px; outline:none; font-family:Arial;
    `);
    (this.nameDom.node as HTMLInputElement).value = this.editName;
    (this.nameDom.node as HTMLInputElement).maxLength = 20;
    (this.nameDom.node as HTMLInputElement).placeholder = '最大20文字';
    this.nameDom.addListener('input').on('input', (e: Event) => {
      this.editName = (e.target as HTMLInputElement).value;
    });
    this.uiElements.push(this.nameDom);

    // 思考レベルボタン
    this.addText(420, 45, '思考レベル:', { fontSize: '14px', color: '#cccccc' });
    this.createLevelButtons(530, 48);

    // 区切り線
    this.uiElements.push(this.add.rectangle(GAME_WIDTH / 2, 70, GAME_WIDTH - 20, 1, 0x333366));

    // スライダー2カラム
    const sliderStartY = 83;
    const sliderRowH = 57;
    const trackW = 185;

    LEFT_SLIDERS.forEach((def, i) => {
      this.createSliderRow(18, sliderStartY + i * sliderRowH, trackW, def);
    });
    RIGHT_SLIDERS.forEach((def, i) => {
      this.createSliderRow(500, sliderStartY + i * sliderRowH, trackW, def);
    });

    // 縦区切り線
    this.uiElements.push(this.add.rectangle(GAME_WIDTH / 2, 260, 1, 360, 0x2a2a55));

    // 区切り線
    this.uiElements.push(this.add.rectangle(GAME_WIDTH / 2, 432, GAME_WIDTH - 20, 1, 0x333366));

    // 保存 / 戻るボタン
    const saveBtn = this.addButton(GAME_WIDTH / 2 - 130, 470, '　保存する　', () => this.handleSave());
    saveBtn.setOrigin(0.5).setColor('#88ff88');

    this.addButton(GAME_WIDTH / 2 + 130, 470, '← 戻る', () => this.showList()).setOrigin(0.5);

    // キャラ種族表示
    this.addText(GAME_WIDTH - 20, 48, `${MONSTER_NAMES[this.editMonsterId] ?? this.editMonsterId}`, {
      fontSize: '13px', color: '#7788aa',
    }).setOrigin(1, 0.5);
  }

  private createLevelButtons(startX: number, y: number): void {
    this.levelButtonTexts = [];
    AI_LEVEL_VALUES.forEach((level, i) => {
      const x = startX + i * 50;
      const isActive = this.editConfig.thinkingLevel === level;
      const btn = this.add.text(x, y, AI_LEVEL_LABELS[level], {
        fontSize: '13px',
        color: isActive ? '#000000' : '#9999cc',
        fontFamily: 'Arial',
        backgroundColor: isActive ? '#88aaff' : '#2a2a55',
        padding: { x: 8, y: 4 },
      }).setInteractive({ useHandCursor: true });
      this.uiElements.push(btn);
      this.levelButtonTexts.push(btn);

      btn.on('pointerdown', () => {
        this.editConfig = { ...this.editConfig, thinkingLevel: level };
        this.refreshLevelButtons();
      });
    });
  }

  private refreshLevelButtons(): void {
    AI_LEVEL_VALUES.forEach((level, i) => {
      const btn = this.levelButtonTexts[i];
      if (!btn) return;
      const isActive = this.editConfig.thinkingLevel === level;
      btn.setColor(isActive ? '#000000' : '#9999cc');
      btn.setBackgroundColor(isActive ? '#88aaff' : '#2a2a55');
    });
  }

  private createSliderRow(x: number, y: number, trackWidth: number, def: SliderDef): void {
    const value = this.editConfig[def.key] as number;
    const isDisabled = def.note === '未実装';

    // ラベル
    const labelColor = isDisabled ? '#555566' : '#cccccc';
    const noteColor = isDisabled ? '#444455' : '#888899';
    this.addText(x, y, def.label, { fontSize: '12px', color: labelColor });
    if (def.note) {
      this.addText(x, y + 14, def.note, { fontSize: '10px', color: noteColor });
    }

    const trackX = x + 120;
    const trackY = y + 22;

    // トラック背景
    const trackBg = this.add.rectangle(trackX, trackY, trackWidth, 5, isDisabled ? 0x222233 : 0x333355).setOrigin(0, 0.5);
    this.uiElements.push(trackBg);

    // フィル（左基点でスケール）
    const fillColor = isDisabled ? 0x334444 : 0x4488ff;
    const fill = this.add.rectangle(trackX, trackY, trackWidth, 5, fillColor).setOrigin(0, 0.5);
    fill.setScale(value / 100, 1);
    this.uiElements.push(fill);
    this.sliderFills.set(def.key, fill);

    // サム（つまみ）
    const thumbX = trackX + (value / 100) * trackWidth;
    const thumbColor = isDisabled ? 0x445555 : 0x88aaff;
    const thumb = this.add.circle(thumbX, trackY, 7, thumbColor);
    this.uiElements.push(thumb);
    this.sliderThumbs.set(def.key, thumb);

    // 値テキスト
    const valText = this.add.text(trackX + trackWidth + 6, trackY, String(value), {
      fontSize: '13px', color: isDisabled ? '#444455' : '#ffffff', fontFamily: 'Arial',
    }).setOrigin(0, 0.5);
    this.uiElements.push(valText);
    this.sliderValueTexts.set(def.key, valText);

    if (isDisabled) return;

    // ヒットエリア
    this.sliderTrackData.set(def.key, { x: trackX, width: trackWidth });
    const hitArea = this.add.rectangle(trackX + trackWidth / 2, trackY, trackWidth, 24, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    this.uiElements.push(hitArea);

    hitArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.draggingKey = def.key;
      this.applySliderValue(def.key, pointer.x, trackX, trackWidth);
    });
  }

  private applySliderValue(key: SliderKey, pointerX: number, trackX: number, trackWidth: number): void {
    const ratio = Phaser.Math.Clamp((pointerX - trackX) / trackWidth, 0, 1);
    const value = Math.round(ratio * 100);
    this.editConfig = { ...this.editConfig, [key]: value };

    const fill = this.sliderFills.get(key);
    if (fill) fill.setScale(Math.max(0.01, value / 100), 1);

    const thumb = this.sliderThumbs.get(key);
    if (thumb) thumb.setX(trackX + (value / 100) * trackWidth);

    const vt = this.sliderValueTexts.get(key);
    if (vt) vt.setText(String(value));
  }

  // ─────────────────────────────────────
  // アクション
  // ─────────────────────────────────────

  private startCreate(monsterId: string): void {
    this.editingChar = null;
    this.editMonsterId = monsterId;
    this.editConfig = { ...getSpeciesAIDefault(monsterId) };
    this.editName = MONSTER_NAMES[monsterId] ?? monsterId;
    this.showParamEditor();
  }

  private startEdit(char: CustomAICharacter): void {
    this.editingChar = char;
    this.editMonsterId = char.monsterId;
    this.editConfig = { ...char.aiConfig };
    this.editName = char.name;
    this.showParamEditor();
  }

  private handleSave(): void {
    if (this.nameDom) {
      this.editName = (this.nameDom.node as HTMLInputElement).value.trim();
    }

    if (!this.editName) {
      this.showMessage('名前を入力してください', '#ff8888');
      return;
    }

    const now = Date.now();
    const char: CustomAICharacter = this.editingChar
      ? { ...this.editingChar, name: this.editName, aiConfig: { ...this.editConfig }, updatedAt: now }
      : {
          id: this.generateId(),
          name: this.editName,
          monsterId: this.editMonsterId,
          aiConfig: { ...this.editConfig },
          createdAt: now,
          updatedAt: now,
        };

    try {
      saveCustomAICharacter(char);
      this.showList();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '保存に失敗しました';
      this.showMessage(msg, '#ff8888');
    }
  }

  private showMessage(msg: string, color: string): void {
    const existing = this.uiElements.find(
      (el) => el instanceof Phaser.GameObjects.Text && (el as Phaser.GameObjects.Text).getData('isMessage')
    );
    if (existing) existing.destroy();

    const text = this.add.text(GAME_WIDTH / 2, 505, msg, {
      fontSize: '15px', color, fontFamily: 'Arial',
    }).setOrigin(0.5).setData('isMessage', true);
    this.uiElements.push(text);
    this.time.delayedCall(2500, () => { if (text.active) text.destroy(); });
  }

  // ─────────────────────────────────────
  // ユーティリティ
  // ─────────────────────────────────────

  private addText(
    x: number, y: number, content: string,
    style: Phaser.Types.GameObjects.Text.TextStyle
  ): Phaser.GameObjects.Text {
    const t = this.add.text(x, y, content, { fontFamily: 'Arial', ...style });
    this.uiElements.push(t);
    return t;
  }

  private addButton(x: number, y: number, label: string, onClick: () => void): Phaser.GameObjects.Text {
    const btn = this.add.text(x, y, label, {
      fontSize: '20px', color: '#ffffff', fontFamily: 'Arial',
      stroke: '#000000', strokeThickness: 3,
    }).setInteractive({ useHandCursor: true });
    btn.on('pointerover', () => btn.setScale(1.08));
    btn.on('pointerout', () => btn.setScale(1.0));
    btn.on('pointerdown', onClick);
    this.uiElements.push(btn);
    return btn;
  }

  private generateId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}
